<template>
  <Teleport v-if="showBulkActions" to="#ingredient-bulk-actions">
    <IngredientBulkActions
      v-if="enableMultipleSelectionMode"
      :ingredient-count="ingredientCount"
      :ingredient-search-store="ingredientSearchStore"
      :ingredient-tag-store="ingredientTagStore"
    />
    <button
      v-else
      class="button is-info is-outlined"
      data-subscriber-feature
      data-pitch="Subscribe to select multiple ingredients for bulk tagging and archiving."
    >
      <span class="icon-text">
        <span class="icon">
          <i class="fas fa-ellipsis-h"></i>
        </span>
        <span>Actions</span>
      </span>
    </button>
  </Teleport>

  <div ref="element" class="ingredient-dashboard">
    <div v-if="showFilters" class="field has-addons">
      <div class="control has-icons-left is-rounded is-flex-grow-1">
        <span class="icon is-left">
          <i class="fas fa-search"></i>
        </span>
        <input
          ref="textInput"
          class="input is-rounded"
          type="search"
          placeholder="Search ingredients by name..."
          :value="ingredientSearchStore.q"
          style="z-index: 3"
          @input="ingredientSearchStore.setQueryFilter(textInput.value)"
        />
      </div>

      <div class="control">
        <button
          title="Advanced Filtering"
          class="button rounded-right"
          :class="{ 'is-active': showAdvanced, 'is-info': showAdvanced }"
          @click="showAdvanced = !showAdvanced"
        >
          <span class="icon is-left">
            <i class="fa-light fa-sliders-h"></i>
          </span>
          <span>Filters</span>
        </button>
      </div>
    </div>

    <div v-show="showAdvanced" class="box">
      <DateRange
        :begin="ingredientSearchStore.beginDate"
        :end="ingredientSearchStore.endDate"
        @update:model-value="ingredientSearchStore.setDateFilter"
      />

      <div class="is-flex is-align-items-center gap-3">
        <IngredientTypeFilter
          :model-value="ingredientSearchStore.ingredientTypes"
          @update:model-value="ingredientSearchStore.setIngredientTypes"
        />

        <TagInput
          v-if="showTagInput"
          :model-value="ingredientSearchStore.activeTags"
          :all-tags="ingredientTagStore.allTags || []"
          :allow-new="false"
          :append-right="true"
          placeholder="Filter by tag..."
          @update:model-value="setTagFilter"
          @advanced="(tag) => tagEditController.show(tag)"
        />

        <TagManager :tag-store="ingredientTagStore" />

        <a
          v-if="hasAnyFilters"
          id="reset-filters"
          class="ml-auto"
          @click.prevent="ingredientSearchStore.resetFilters()"
        >
          <span class="icon-text">
            <span class="icon has-text-danger">
              <i class="fa fa-times"></i>
            </span>
            <span class="is-bold">Reset Filters</span>
          </span>
        </a>
      </div>

      <TagEditDialog
        v-if="editingTag"
        :id="editingTag.id"
        :name="editingTag.name"
        :color="editingTag.color"
        :count="editingTag.count"
        @save="(tag) => tagEditController.save(tag)"
        @cancel="(tag) => tagEditController.cancel(tag)"
        @destroy="(tag) => tagEditController.destroy(tag)"
      />
    </div>

    <div v-if="ingredientSearchStore.hasAnyArchived" class="filter-archived">
      <ArchivedOnlyCheckbox
        :model-value="ingredientSearchStore.archived"
        @update:model-value="ingredientSearchStore.setArchived"
      />
    </div>

    <div v-if="showAllArchivedHint" class="has-text-centered" style="padding: 1.5rem 0">
      <p class="mb-3"><em>All of your ingredients are archived.</em></p>
      <button class="button is-info is-outlined" @click="ingredientSearchStore.setArchived(true)">
        Show archived ingredients
      </button>
    </div>

    <div
      v-if="enableMultipleSelectionMode"
      class="is-fullwidth is-flex is-flex-direction-row is-justify-content-space-between row-underneath-searchbar"
    >
      <div class="is-flex has-flex-vcentered">
        <button
          v-if="clearSelectionEnabled"
          class="button is-small mr-3 is-info"
          style="border-radius: 3px"
          @click="handleClearSelection"
        >
          Clear Selection
        </button>
        <div
          v-if="ingredientSearchStore.selection.length > 0"
          class="number-selected is-small-mobile"
        >
          {{ ingredientSearchStore.selection.length.toLocaleString() }}
          <span class="is-hidden-mobile">ingredients</span>
          selected
        </div>
      </div>
    </div>

    <IngredientListStatic
      v-if="ingredientSearchStore.loaded && !showAllArchivedHint"
      :ingredients="ingredientSearchStore.visibleIngredients"
      :user-id="userId"
      :show-tags="true"
      show-lock-status
      :allow-locking="allowLocking"
      :allow-inline-tag-edits="allowInlineTagEdits"
      :selection-mode="enableMultipleSelectionMode"
      :selection="ingredientSearchStore.selection"
      @sort-click="sortClick"
      @tag-click="handleTagClick"
      @clear-search="ingredientSearchStore.resetSearch"
      @toggle-selection="ingredientSearchStore.toggleSelection"
      @select-all="handleSelectAll(true)"
      @deselect-all="handleSelectAll(false)"
    />

    <PagerWidget
      v-if="ingredientSearchStore.loaded && !showAllArchivedHint"
      :model-value="ingredientSearchStore.currentPage"
      :total-entries="ingredientSearchStore.totalEntries"
      :per-page="ingredientSearchStore.perPage"
      @update:model-value="changePage"
    />
  </div>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { onBeforeMount, onMounted, onBeforeUnmount, watch } from "vue";
  import { useEventListener } from "@vueuse/core";

  import ArchivedOnlyCheckbox from "./archived-only-checkbox.vue";
  import DateRange from "./date-range.vue";
  import IngredientTypeFilter from "./ingredient-type-filter.vue";
  import TagInput from "./tag-input.vue";
  import TagEditDialog from "./tag-edit-dialog.vue";
  import TagManager from "./tag-manager.vue";
  import PagerWidget from "./pager-widget.vue";
  import IngredientListStatic from "./ingredient-list-static.vue";
  import IngredientBulkActions from "./bulk-manage-ingredients/bulk-actions.vue";

  import { useTagEditController } from "./lib/tag-edit-controller.js";
  import { tagsWithUpdatedTag } from "./stores/tag-store.js";
  import useSearchStoreHistory from "./lib/use-search-store-history.js";
  import useViewLoadTracking from "./lib/use-view-load-tracking.js";
  import { useIngredientSearchStore } from "./stores/ingredient-search-store.js";
  import { useIngredientTagStore } from "./stores/ingredient-tag-store.js";

  const props = defineProps({
    perPage: { type: Number, default: 10 },
    userId: { type: String, required: true },
    ingredientCount: { type: Number, required: true },
    userIsSubscribed: { type: Boolean, default: false },
    allowInlineTagEdits: { type: Boolean, default: false },
    allowLocking: { type: Boolean, default: false },
  });

  const ingredientSearchStore = useIngredientSearchStore.ingredientDashboard();
  const ingredientTagStore = useIngredientTagStore();

  useViewLoadTracking(() => ingredientSearchStore.loaded);

  onBeforeMount(() => {
    ingredientSearchStore.setUserId(props.userId);
    ingredientSearchStore.assignInitial({ perPage: props.perPage }, { patch: true });

    ingredientTagStore.setUserId(props.userId);

    const loadFromUrl = useSearchStoreHistory(ingredientSearchStore, "ingredient_", ["food_mode"]);
    useEventListener(window, "popstate", () => {
      loadFromUrl();
    });
    loadFromUrl();
    ingredientTagStore.loadAllTags();
  });

  const element = _ref(null);
  let textInput = _ref(null);
  let showAdvanced = _ref(ingredientSearchStore.hasAdvancedFilters);
  let editingTag = _ref(null);

  const tagEditController = useTagEditController({
    store: ingredientTagStore,
    editingTag: (editingTag),
    tags: null,
  });

  const showTagInput = _computed(() => {
    if (ingredientSearchStore?.activeTags?.length > 0) {
      return true;
    }
    return ingredientTagStore?.allTags?.length > 0;
  });

  const setTagFilter = (tags) => ingredientSearchStore.setTagFilter(tags);

  const handleTagClick = (tag) => {
    const alreadyActive = ingredientSearchStore.activeTags.some((t) => t.name === tag.name);
    if (!alreadyActive) {
      setTagFilter([...ingredientSearchStore.activeTags, tag]);
      showAdvanced.value = true;
    }
  };

  const sortClick = (field) => {
    ingredientSearchStore.toggleSort(field);
  };

  const changePage = (newPage) => {
    ingredientSearchStore.loadIngredientPage(newPage);
    element.value.scrollIntoView({ behavior: "smooth" });
  };

  const showFilters = _computed(() => {
    return (
      props.ingredientCount > props.perPage ||
      ingredientSearchStore.hasAnyArchived ||
      ingredientSearchStore.hasQuery ||
      ingredientSearchStore.hasAdvancedFilters
    );
  });

  const hasAnyFilters = _computed(() => {
    return (
      ingredientSearchStore.hasQuery ||
      ingredientSearchStore.hasAdvancedFilters ||
      ingredientSearchStore.archived
    );
  });

  useEventListener(document, "ingredient-tag:update", (ev) => {
    const { oldTag, newTag } = ev.detail;
    ingredientSearchStore.activeTags = tagsWithUpdatedTag(
      ingredientSearchStore.activeTags,
      oldTag,
      newTag
    );
  });

  watch(
    () => ingredientSearchStore.hasAdvancedFilters,
    (hasAdvancedFilters) => {
      if (hasAdvancedFilters) {
        showAdvanced.value = true;
      }
    }
  );

  // Shown when the user has only archived ingredients and is in the active view.
  const showAllArchivedHint = _computed(() => {
    return (
      ingredientSearchStore.loaded &&
      !ingredientSearchStore.archived &&
      ingredientSearchStore.hasAnyArchived &&
      ingredientSearchStore.totalEntries === 0 &&
      !ingredientSearchStore.hasQuery &&
      !ingredientSearchStore.beginDate &&
      !ingredientSearchStore.endDate
    );
  });

  const showBulkActions = _computed(() => props.ingredientCount > props.perPage);

  const enableMultipleSelectionMode = _computed(() =>
    Boolean(props.userIsSubscribed && showBulkActions.value)
  );

  const clearSelectionEnabled = _computed(() => ingredientSearchStore.selection.length > 0);

  const handleClearSelection = () => {
    ingredientSearchStore.clearSelection();
  };

  async function handleSelectAll(isAdding) {
    const allIds = await ingredientSearchStore.fetchSelection();
    if (isAdding) {
      ingredientSearchStore.addToSelection(allIds);
    } else {
      ingredientSearchStore.removeFromSelection(allIds);
    }
  }

  async function handleArchiveEvent(event) {
    const affectedIds = event.detail?.affectedIds;
    const nowArchived = event.type === "archived";

    if (affectedIds !== undefined) {
      // Bulk: mirror recipe behavior. Switch to the view containing the
      // affected rows and keep them selected so the user sees what just moved.
      ingredientSearchStore.clearSelection();
      ingredientSearchStore.archived = nowArchived;
      await ingredientSearchStore.loadIngredientPage(1);
      if (affectedIds.length) ingredientSearchStore.addToSelection(affectedIds);
      return;
    }

    // Single ingredient: stay in the current filter. If the archived row was
    // the only one on a non-first page, reloading the same page would land on
    // a blank out-of-range page (will_paginate doesn't clamp). Jump to page 1.
    const itemLeftView =
      (nowArchived && !ingredientSearchStore.archived) ||
      (!nowArchived && ingredientSearchStore.archived);
    const willStrand =
      itemLeftView &&
      ingredientSearchStore.currentPage > 1 &&
      ingredientSearchStore.visibleIngredients.length <= 1;

    ingredientSearchStore.clearSelection();
    ingredientSearchStore.loadIngredientPage(willStrand ? 1 : undefined);
  }

  onMounted(() => {
    const modal = document.getElementById("archive-ingredient-modal");
    if (modal) {
      modal.addEventListener("archived", handleArchiveEvent);
      modal.addEventListener("restored", handleArchiveEvent);
    }
  });

  onBeforeUnmount(() => {
    const modal = document.getElementById("archive-ingredient-modal");
    if (modal) {
      modal.removeEventListener("archived", handleArchiveEvent);
      modal.removeEventListener("restored", handleArchiveEvent);
    }
  });
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  $spacer: 0.5rem;

  .ingredient-dashboard {
    display: flex;
    flex-direction: column;
  }

  .filter-archived {
    position: relative;

    & > label {
      position: absolute;
      top: -0.5rem;
      right: 0;

      @include until($widescreen) {
        top: -0.25rem;
      }

      @include mobile {
        top: 0.625rem;
      }

      @media screen and (max-width: 320px) {
        top: 0.875rem;
      }
    }
  }

  .row-underneath-searchbar {
    margin-top: -0.5 * $spacer;
    margin-bottom: $spacer;

    @include until($widescreen) {
      margin-top: $spacer;
    }
  }

  .number-selected {
    @include until($desktop) {
      font-style: italic;
    }
  }
</style>
